import React, { memo, ReactNode, useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Typography, AutocompleteRenderInputParams, Autocomplete } from '@mui/material'
import { FieldInputProps, useField } from 'formik'

import { Address, FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useAddressSuggestions } from '../../utils/hooks/useAddressSuggestions'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { TextField } from '../../utils/shared/TextField'
import { TextFieldLabel } from '../../utils/shared/TextFieldLabel'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

export type AddressFieldProps = {
  className?: string
  error?: string
  name: string
  label: ReactNode
  required?: boolean
  placesEnabled?: boolean
  helperText?: ReactNode
  placeholder?: string
  onReceiveAddress: (address: Address) => void
}

const AddressFieldBase = memo(
  ({
    name,
    value: externalValue,
    error,
    label,
    placeholder,
    placesEnabled = false,
    required,
    helperText,
    onBlur,
    onChange,
    onReceiveAddress,
  }: AddressFieldProps & FieldInputProps<string>) => {
    const { t } = useTranslation()
    const [divRef, setDivRef] = useState<HTMLDivElement | null>(null)

    const { value, options, handleInputChange, handleChange } = useAddressSuggestions({
      name,
      containerElement: divRef,
      value: externalValue,
      onChange,
      onReceiveAddress,
    })

    const getOptionLabel = (option: google.maps.places.AutocompletePrediction | string) => {
      return typeof option === 'string' ? option : option.description
    }

    // The `PlacesService` requires an HTMLElement to initialize the service, so
    // we added this div only for this purpose. We also had to save the element reference
    // as a `state` instead of `ref` because wee need to trigger an update to create the service
    // when the reference is set, that's why we do `ref={setDivRef}`.
    const placesServiceContainer = <div ref={setDivRef} />

    const renderOption = (
      props: React.HTMLAttributes<HTMLElement>,
      option: google.maps.places.AutocompletePrediction
    ) => {
      return (
        <Box {...props}>
          <Typography>{option.structured_formatting.main_text}</Typography>

          {option.structured_formatting.secondary_text && (
            <Typography>{option.structured_formatting.secondary_text}</Typography>
          )}
        </Box>
      )
    }

    const renderInput = (props: AutocompleteRenderInputParams) => (
      <TextField
        {...props}
        error={Boolean(error)}
        name={name}
        placeholder={
          placeholder
            ? t(placeholder) || ''
            : t(`${label}_placeholder`, {
                defaultValue: '',
                postProcess: [],
              }) || ''
        }
        InputLabelProps={{
          id: `${name}-label`,
        }}
        InputProps={{
          ...props.InputProps,
          style: {
            padding: '22px 16px 3px',
          },
          className: undefined,
        }}
        inputProps={{
          ...props.inputProps,
          style: {
            padding: 0,
          },
          'aria-required': required,
          'aria-labelledby': `${name}-label`,
        }}
        label={
          <TextFieldLabel required={required}>
            <Translate>{label}</Translate>
          </TextFieldLabel>
        }
      />
    )

    return (
      <Box>
        <Autocomplete
          options={options}
          inputValue={value}
          getOptionLabel={getOptionLabel}
          onInputChange={handleInputChange}
          onChange={handleChange}
          renderOption={renderOption}
          renderInput={renderInput}
          onBlur={onBlur}
          autoHighlight
          disableClearable
          disablePortal
          freeSolo
        />

        <FieldHelperText helperText={helperText} error={error} name={name} />

        {placesEnabled && placesServiceContainer}
      </Box>
    )
  }
)

AddressFieldBase.displayName = 'AddressFieldBase'

export const AddressField = (props: AddressFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.value, formHelpers as FormHelpersProviderReturn, e)
      field.onChange(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <AddressFieldBase error={hasError ? meta.error : undefined} {...props} {...field} onChange={handleChange} />
}
